import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isSorApiError,
  isSorAuthError,
  useSorApi,
  type SorFacet,
  type SorQueryPayload,
  type SorRecord,
} from '../hooks/useSorApi';
import {
  DEFAULT_MY_WORK_FIELDS,
  QUEUE_ITEM_HISTORY_DEFINITION,
  buildStepRollbackData,
  buildMyMembershipsQuery,
  buildMyQueuesQuery,
  buildMyUserQuery,
  buildMyWorkListQuery,
  buildCreatableEntitiesQuery,
  buildQueueActionsQuery,
  buildQueueItemHistoryLookupQuery,
  buildQueueItemHistoryRecord,
  buildQueueMembersQuery,
  buildQueueOperationStep,
  buildTypeCountsQuery,
  getAvailableActions,
  mapMyWorkItem,
  parseCreatableEntities,
  parseMemberships,
  parseMyUser,
  parseQueueActions,
  parseQueueMembers,
  parseQueueTypes,
  parseTypeCounts,
  parseTypeDefinition,
  type MyWorkAction,
  type MyWorkFieldConfig,
  type MyWorkItem,
  type MyWorkMemberships,
  type MyWorkSort,
  type MyWorkType,
  type MyWorkTypeDefinition,
  type MyWorkUser,
  type QueueActionData,
  type QueueMemberOption,
  type QueueOperation,
  type SorProjectedRow,
  type StateCategory,
} from './myWorkSor';
import type { QueueEntityRecord } from './myWorkQueueSor';
import type { WorkQueueCreateOption } from './myWorkQueueTypes';

const DEFAULT_PAGE_SIZE = 20;
// Wait for the user to stop typing before querying
const SEARCH_DEBOUNCE_MS = 400;
// Offset paging (any explicit sort) values every matching row, so it stops here; search narrows instead
const MAX_OFFSET_DEPTH = 500;
// Routing updates the queue item a moment after the record changes
const ROUTING_SETTLE_MS = 1500;

// Auth0 errors that signing in again can't fix, so Connect would only reopen a failing popup
const NON_CONNECTABLE_AUTH_CODES = new Set(['access_denied', 'unauthorized_client']);

export interface MyWorkFilters {
  /** Status tile: the record's progress */
  statusCategory: StateCategory | null;
  sourceStates: string[];
  customer: string | null;
  search: string;
  sort: MyWorkSort;
}

export const DEFAULT_MY_WORK_FILTERS: MyWorkFilters = {
  statusCategory: null,
  sourceStates: [],
  customer: null,
  search: '',
  sort: 'newest',
};

/** Why the view has nothing to list, before any list query runs */
export type MyWorkEmptyReason = 'no-profile' | 'no-queues' | 'no-types';

export interface MyWorkLoadError {
  message: string;
  /** Signing in to the SOR audience (connectSor from a click) can fix it */
  needsConnect: boolean;
}

/** What A2, A3, B1, B2 and A4 return, loaded once per session */
export interface MyWorkContext extends MyWorkMemberships {
  user: MyWorkUser;
  types: MyWorkType[];
  /** definitionId -> queue items of that type */
  typeCounts: Record<string, number>;
  actions: MyWorkAction[];
}

type ListResponse = {
  items: SorProjectedRow[];
  total: number;
  total_capped: boolean;
  next_cursor: string | null;
  facets?: Record<string, SorFacet> | null;
};

type NextPage = { cursor: string } | { offset: number } | null;

export function toMyWorkLoadError(error: unknown, fallback: string): MyWorkLoadError {
  return {
    message: describeSorError(error, fallback),
    needsConnect: isSorAuthError(error) && !NON_CONNECTABLE_AUTH_CODES.has(error.code),
  };
}

/** A user-facing message for the error codes the My Work calls can return */
export function describeSorError(error: unknown, fallback: string): string {
  if (isSorApiError(error)) {
    switch (error.code) {
      case 'revision_conflict':
        return 'Someone else changed this item. The list has been refreshed.';
      case 'related_unavailable':
        return 'You do not have access to a record type this view reads.';
      case 'masked_field_query':
        return 'You cannot filter or sort by a field that is masked for you.';
      case 'validation_failed':
      case 'invalid_query': {
        const detail = error.details.map((item) => item.message).filter(Boolean).join('; ');
        return detail || error.apiMessage || fallback;
      }
      default:
        return error.apiMessage || fallback;
    }
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

const newEventId = (): string => globalThis.crypto.randomUUID();

export interface UseMyWorkOptions {
  /** Source fields the list reads; defaults to the inspection's */
  fields?: MyWorkFieldConfig;
  pageSize?: number;
  /** Receives the signed-in user's directory record (with roles) each time A2 loads it */
  onUserLoaded?: (record: SorRecord) => void;
}

type DefinitionResponse = Parameters<typeof parseTypeDefinition>[0];

/**
 * Runs the My Work flow against SOR: the per-session context calls, one list request per screen
 * change (with status tile and dropdown counts from facets), and the assignment and card-action writes.
 */
export function useMyWork({
  fields = DEFAULT_MY_WORK_FIELDS,
  pageSize = DEFAULT_PAGE_SIZE,
  onUserLoaded,
}: UseMyWorkOptions = {}) {
  const { queryRecords, sorFetch, connectSor } = useSorApi();
  // Both change identity with the Auth0 session; read them through refs so that doesn't refetch
  const queryRecordsRef = useRef(queryRecords);
  queryRecordsRef.current = queryRecords;
  const sorFetchRef = useRef(sorFetch);
  sorFetchRef.current = sorFetch;
  const onUserLoadedRef = useRef(onUserLoaded);
  onUserLoadedRef.current = onUserLoaded;

  // GET /definitions/{key} per key, shared by the Create check and A1 so a type's definition loads once
  // per session. Not tied to a caller's signal, since another caller may still be waiting on it
  const definitionRequestsRef = useRef(new Map<string, Promise<DefinitionResponse>>());
  const fetchDefinition = useCallback((key: string): Promise<DefinitionResponse> => {
    const requests = definitionRequestsRef.current;
    let request = requests.get(key);
    if (!request) {
      const created = sorFetchRef.current<DefinitionResponse>(`/api/v1/definitions/${encodeURIComponent(key)}`);
      // A failed load is tried again next time
      created.catch(() => {
        if (requests.get(key) === created) {
          requests.delete(key);
        }
      });
      requests.set(key, created);
      request = created;
    }
    return request;
  }, []);

  const [context, setContext] = useState<MyWorkContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState<MyWorkLoadError | null>(null);
  const [emptyReason, setEmptyReason] = useState<MyWorkEmptyReason | null>(null);
  const [contextKey, setContextKey] = useState(0);

  const [typeKey, setTypeKey] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<Record<string, MyWorkTypeDefinition>>({});
  const [definitionError, setDefinitionError] = useState<MyWorkLoadError | null>(null);

  const [filters, setFilters] = useState<MyWorkFilters>(DEFAULT_MY_WORK_FILTERS);
  const [search, setSearch] = useState('');

  const [items, setItems] = useState<MyWorkItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [facets, setFacets] = useState<Record<string, SorFacet> | null>(null);
  const [nextPage, setNextPage] = useState<NextPage>(null);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<MyWorkLoadError | null>(null);
  const [listKey, setListKey] = useState(0);
  const [createOptions, setCreateOptions] = useState<WorkQueueCreateOption[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(filters.search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // A2 → A3 → B1 + B2 + A4, once per session (and on Retry)
  useEffect(() => {
    const controller = new AbortController();
    const query = <T,>(payload: SorQueryPayload) => queryRecordsRef.current<T>(payload, controller.signal);

    // The option when its definition grants the user can_create; a failure leaves it out
    const checkCanCreate = (option: WorkQueueCreateOption): Promise<WorkQueueCreateOption | null> =>
      fetchDefinition(option.key || option.definitionId)
        .then((definitionOut) => (parseTypeDefinition(definitionOut).canCreate ? option : null))
        .catch((error) => {
          if (!controller.signal.aborted) {
            console.warn(`Failed to check create permission for ${option.label}`, error);
          }
          return null;
        });

    // Create buttons: one per enabled entity type whose definition grants the user can_create.
    // Loaded beside the queue calls; a failure only leaves that type's button (or all) out
    const loadCreateOptions = () =>
      query<{ items: QueueEntityRecord[] }>(buildCreatableEntitiesQuery())
        .then((response) => Promise.all(parseCreatableEntities(response?.items ?? []).map(checkCanCreate)))
        .then((options) => {
          if (!controller.signal.aborted) {
            setCreateOptions(options.filter((option): option is WorkQueueCreateOption => option !== null));
          }
        })
        .catch((error) => {
          if (!controller.signal.aborted) {
            console.warn('Failed to load the entity types to create', error);
          }
        });

    const load = async () => {
      setContextLoading(true);
      setContextError(null);
      setEmptyReason(null);
      setCreateOptions([]);
      try {
        const userResponse = await query<{ items: SorRecord[] }>(buildMyUserQuery());
        const userRecord = userResponse?.items?.[0];
        if (userRecord && !controller.signal.aborted) {
          try {
            onUserLoadedRef.current?.(userRecord);
          } catch (error) {
            console.warn('onUserLoaded failed', error);
          }
        }
        const user = parseMyUser(userResponse?.items as Parameters<typeof parseMyUser>[0]);
        if (!user) {
          setContext(null);
          setEmptyReason('no-profile');
          return;
        }
        void loadCreateOptions();
        const membershipResponse = await query<{ items: SorProjectedRow[] }>(buildMyMembershipsQuery(user.roleIds));
        const memberships = parseMemberships(membershipResponse?.items ?? [], user);
        if (memberships.queueIds.length === 0) {
          setContext({ user, ...memberships, types: [], typeCounts: {}, actions: [] });
          setEmptyReason('no-queues');
          return;
        }
        const [queues, actions] = await Promise.all([
          query<{ items: SorProjectedRow[] }>(buildMyQueuesQuery(memberships.queueIds)),
          query<{ items: SorRecord<QueueActionData>[] }>(buildQueueActionsQuery(memberships.queueIds)),
        ]);
        const types = parseQueueTypes(queues?.items ?? []);
        setContext({
          user,
          ...memberships,
          types,
          typeCounts: {},
          actions: parseQueueActions(actions?.items ?? []),
        });
        setEmptyReason(types.length === 0 ? 'no-types' : null);
        setTypeKey((current) => (current && types.some((type) => type.key === current) ? current : types[0]?.key ?? null));
        // Optional tab counts, filled in when they arrive; only shown when there are tabs (2+ types)
        if (types.length > 1) {
          query<ListResponse>(buildTypeCountsQuery(memberships.queueIds))
            .then((counts) => {
              if (!controller.signal.aborted) {
                const typeCounts = parseTypeCounts(counts?.facets);
                setContext((current) => (current ? { ...current, typeCounts } : current));
              }
            })
            .catch((error) => {
              if (!controller.signal.aborted) {
                console.warn('Failed to count work per type', error);
              }
            });
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        console.error('Failed to load My Work', error);
        setContext(null);
        setContextError(toMyWorkLoadError(error, 'Failed to load your work queues.'));
      } finally {
        if (!controller.signal.aborted) {
          setContextLoading(false);
        }
      }
    };

    void load();
    return () => controller.abort();
  }, [contextKey, fetchDefinition]);

  const selectedType = useMemo(
    () => context?.types.find((type) => type.key === typeKey) ?? null,
    [context, typeKey]
  );
  const definition = typeKey ? definitions[typeKey] ?? null : null;

  // A1: the selected type's definition, loaded when its tab opens
  useEffect(() => {
    if (!typeKey || definitions[typeKey]) {
      return undefined;
    }
    const controller = new AbortController();
    setDefinitionError(null);
    // Usually already loaded (or loading) by the Create check
    fetchDefinition(typeKey)
      .then((response) => {
        if (!controller.signal.aborted) {
          setDefinitions((current) => ({ ...current, [typeKey]: parseTypeDefinition(response) }));
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load the type definition', error);
          setDefinitionError(toMyWorkLoadError(error, 'Failed to load this work type.'));
        }
      });
    return () => controller.abort();
  }, [typeKey, definitions, fetchDefinition]);

  const listQuery = useMemo(() => {
    if (!context || !selectedType || !definition) {
      return null;
    }
    return buildMyWorkListQuery({
      type: selectedType,
      definition,
      queueIds: context.queueIds,
      statusCategory: filters.statusCategory,
      sourceStates: filters.sourceStates,
      customer: filters.customer,
      search,
      sort: filters.sort,
      fields,
    });
    // filters.search is read through the debounced `search`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context,
    selectedType,
    definition,
    filters.statusCategory,
    filters.sourceStates,
    filters.customer,
    filters.sort,
    search,
    fields,
  ]);

  const nextPageFrom = useCallback(
    (response: ListResponse, loaded: number): NextPage => {
      if (filters.sort === 'newest') {
        return response.next_cursor ? { cursor: response.next_cursor } : null;
      }
      const hasMore = loaded < response.total && loaded < MAX_OFFSET_DEPTH;
      return hasMore ? { offset: loaded } : null;
    },
    [filters.sort]
  );

  // One request per screen change: the page, the total and every tile/dropdown count
  useEffect(() => {
    if (!listQuery) {
      setItems([]);
      setTotal(0);
      setFacets(null);
      setNextPage(null);
      return undefined;
    }
    const controller = new AbortController();
    setListLoading(true);
    setListError(null);
    const page = filters.sort === 'newest' ? { limit: pageSize } : { limit: pageSize, offset: 0 };
    queryRecordsRef
      .current<ListResponse>({ ...listQuery, page }, controller.signal)
      .then((response) => {
        const rows = response?.items ?? [];
        setItems(rows.map((row) => mapMyWorkItem(row, fields)));
        setTotal(response?.total ?? 0);
        setTotalCapped(!!response?.total_capped);
        setFacets(response?.facets ?? null);
        setNextPage(response ? nextPageFrom(response, rows.length) : null);
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        console.error('Failed to load work items', error);
        setItems([]);
        setTotal(0);
        setFacets(null);
        setNextPage(null);
        setListError(toMyWorkLoadError(error, 'Failed to load work items.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setListLoading(false);
        }
      });
    return () => controller.abort();
  }, [listQuery, listKey, pageSize, fields, filters.sort, nextPageFrom]);

  const loadMore = useCallback(async () => {
    if (!listQuery || !nextPage || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const page = 'cursor' in nextPage ? { limit: pageSize, cursor: nextPage.cursor } : { limit: pageSize, offset: nextPage.offset };
      const response = await queryRecordsRef.current<ListResponse>({ ...listQuery, page });
      const rows = response?.items ?? [];
      const loaded = items.length + rows.length;
      setItems((current) => [...current, ...rows.map((row) => mapMyWorkItem(row, fields))]);
      setNextPage(response && rows.length ? nextPageFrom(response, loaded) : null);
    } catch (error) {
      console.error('Failed to load more work items', error);
      setListError(toMyWorkLoadError(error, 'Failed to load more work items.'));
    } finally {
      setLoadingMore(false);
    }
  }, [listQuery, nextPage, loadingMore, pageSize, items.length, fields, nextPageFrom]);

  const refresh = useCallback(() => setListKey((key) => key + 1), []);
  /** After a record was created or changed elsewhere: routing updates its queue item a moment later */
  const refreshAfterRouting = useCallback(() => {
    refresh();
    setTimeout(refresh, ROUTING_SETTLE_MS);
  }, [refresh]);
  const reload = useCallback(() => {
    definitionRequestsRef.current.clear();
    setDefinitions({});
    setContextKey((key) => key + 1);
    setListKey((key) => key + 1);
  }, []);

  /** Runs from a click, so the Auth0 consent/login popup isn't blocked */
  const connect = useCallback(async () => {
    try {
      await connectSor();
      reload();
    } catch (error) {
      setContextError(toMyWorkLoadError(error, 'Could not connect.'));
    }
  }, [connectSor, reload]);

  const updateFilters = useCallback((patch: Partial<MyWorkFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters((current) => ({ ...DEFAULT_MY_WORK_FILTERS, sort: current.sort }));
  }, []);

  const selectType = useCallback((key: string) => {
    setTypeKey(key);
    // States and customers belong to the previous type
    setFilters((current) => ({ ...current, sourceStates: [], customer: null, statusCategory: null }));
  }, []);

  const actionsFor = useCallback(
    (item: MyWorkItem): MyWorkAction[] =>
      context && definition
        ? getAvailableActions(context.actions, item, {
            roleByQueue: context.roleByQueue,
            membershipByQueue: context.membershipByQueue,
            definition,
          })
        : [],
    [context, definition]
  );

  // History is append-only and a repeated eventId is refused, so a failed POST is never resent. When
  // the response was lost (no HTTP status), the record is read back by eventId to see whether it was stored
  const recordHistory = useCallback(async (record: ReturnType<typeof buildQueueItemHistoryRecord>) => {
    try {
      await sorFetchRef.current(`/api/v1/records?definition=${encodeURIComponent(QUEUE_ITEM_HISTORY_DEFINITION)}`, {
        method: 'POST',
        body: JSON.stringify(record),
      });
    } catch (error) {
      if (isSorApiError(error)) {
        console.warn(`Queue item history ${record.external_id} was refused`, error);
        return;
      }
      try {
        const stored = await queryRecordsRef.current<{ total?: number }>(buildQueueItemHistoryLookupQuery(record.external_id));
        if (!stored?.total) {
          console.warn(`Queue item history ${record.external_id} was not stored`, error);
        }
      } catch (lookupError) {
        console.warn(`Could not check queue item history ${record.external_id}`, lookupError);
      }
    }
  }, []);

  const transitionRecord = useCallback(
    (recordId: string, to: string, expectedRevision: number, comment?: string | null) =>
      sorFetchRef.current<SorRecord>(`/api/v1/records/${recordId}/transition`, {
        method: 'POST',
        body: JSON.stringify({ to, expected_revision: expectedRevision, ...(comment ? { comment } : {}) }),
      }),
    []
  );

  /**
   * One §3 step: PATCH the step's fields, transition the state with the revision the PATCH returned,
   * then record the history. A transition that fails after the PATCH is retried once; if it still
   * can't move, the PATCH is undone, so e.g. a claim doesn't leave the item both mine and unassigned.
   */
  const runQueueOperation = useCallback(
    async (
      item: MyWorkItem,
      operation: QueueOperation,
      input: { memberId?: string | null; memberUserId?: string | null; waitingReason?: string | null; actionId?: string } = {}
    ): Promise<void> => {
      if (!context) {
        throw new Error('My Work is not loaded yet.');
      }
      const memberId = operation === 'CLAIM' ? context.membershipByQueue[item.queueId] : input.memberId;
      if ((operation === 'CLAIM' || operation === 'ASSIGN') && !memberId) {
        throw new Error('No queue membership to assign this item to.');
      }
      const step = buildQueueOperationStep(operation, {
        memberId,
        userId: context.user.id,
        waitingReason: input.waitingReason,
      });
      try {
        const patched = await sorFetchRef.current<SorRecord>(`/api/v1/records/${item.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ data: step.data, expected_revision: item.revision }),
        });

        let moved: SorRecord;
        try {
          moved = await transitionRecord(item.id, step.to, patched.revision);
        } catch (firstError) {
          // A rule failure (422) or a conflict won't pass on a retry
          const retryable = !isSorApiError(firstError) || firstError.status >= 500;
          try {
            if (!retryable) {
              throw firstError;
            }
            moved = await transitionRecord(item.id, step.to, patched.revision);
          } catch (error) {
            // The item didn't move, so its fields go back to what they were (for a claim: cleared)
            await sorFetchRef
              .current(`/api/v1/records/${item.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ data: buildStepRollbackData(step, item), expected_revision: patched.revision }),
              })
              .catch((rollbackError) => console.warn('Failed to undo the fields after a failed transition', rollbackError));
            throw error;
          }
        }

        let toAssignee: Parameters<typeof buildQueueItemHistoryRecord>[0]['toAssignee'] = null;
        if (operation === 'CLAIM') {
          toAssignee = { memberId, userId: context.user.id };
        } else if (operation === 'ASSIGN') {
          toAssignee = { memberId, userId: input.memberUserId ?? null };
        }
        const fromAssignee =
          operation === 'RELEASE' ? { memberId: item.assigneeMemberId, userId: item.claimedByUserId } : null;
        const eventId = newEventId();
        await recordHistory(
          buildQueueItemHistoryRecord({
            eventId,
            eventType: step.eventType,
            item: {
              id: item.id,
              revision: moved.revision,
              queueId: item.queueId,
              queueVisitId: item.queueVisitId,
            },
            fromState: item.state,
            toState: step.to,
            toAssignee,
            fromAssignee,
            actionId: input.actionId,
            reason: operation === 'WAIT' ? input.waitingReason : null,
          })
        );
      } finally {
        // On success the item moved; on a conflict someone else moved it (e.g. it's no longer READY)
        refresh();
      }
    },
    [context, transitionRecord, recordHistory, refresh]
  );

  /** ENTITY_TRANSITION, e.g. Cancel: moves the source record; routing then updates the queue item */
  const runEntityTransition = useCallback(
    async (item: MyWorkItem, action: MyWorkAction, comment?: string | null): Promise<void> => {
      if (!item.source || !action.data.targetState) {
        throw new Error('This action has no target state.');
      }
      try {
        await transitionRecord(item.source.id, action.data.targetState, item.source.revision, comment);
      } finally {
        refresh();
        setTimeout(refresh, ROUTING_SETTLE_MS);
      }
    },
    [transitionRecord, refresh]
  );

  /** Records a delivered MANUAL_ACKNOWLEDGEMENT (e.g. a reminder the app sent) in the item's history */
  const recordActionSucceeded = useCallback(
    async (item: MyWorkItem, action: MyWorkAction): Promise<void> => {
      const eventId = newEventId();
      await recordHistory(
        buildQueueItemHistoryRecord({
          eventId,
          eventType: 'ACTION_SUCCEEDED',
          item: { id: item.id, revision: item.revision, queueId: item.queueId, queueVisitId: item.queueVisitId },
          actionId: action.id,
          actionOutcome: 'SUCCEEDED',
        })
      );
    },
    [recordHistory]
  );

  /** Members of the item's queue, for the Assign picker */
  const loadQueueMembers = useCallback(async (queueId: string): Promise<QueueMemberOption[]> => {
    const response = await queryRecordsRef.current<{ items: SorProjectedRow[] }>(buildQueueMembersQuery(queueId));
    return parseQueueMembers(response?.items ?? []);
  }, []);

  /** Creates a record of the type; routing adds its queue item a moment later */
  const createRecord = useCallback(
    async (type: MyWorkType, data: Record<string, unknown>): Promise<SorRecord> => {
      const record = await sorFetchRef.current<SorRecord>(`/api/v1/records?definition=${encodeURIComponent(type.key)}`, {
        method: 'POST',
        body: JSON.stringify({ data }),
      });
      refreshAfterRouting();
      return record;
    },
    [refreshAfterRouting]
  );

  return {
    context,
    contextLoading,
    loadError: contextError ?? definitionError ?? listError,
    emptyReason,
    /** One per enabled entity type whose definition grants can_create */
    createOptions,
    types: context?.types ?? [],
    typeCounts: context?.typeCounts ?? {},
    selectedType,
    definition,
    selectType,
    filters,
    updateFilters,
    clearFilters,
    items,
    total,
    totalCapped,
    facets,
    hasMore: !!nextPage,
    listLoading: contextLoading || (!!selectedType && !definition && !definitionError) || listLoading,
    loadingMore,
    loadMore,
    refresh,
    refreshAfterRouting,
    reload,
    connect,
    actionsFor,
    runQueueOperation,
    runEntityTransition,
    recordActionSucceeded,
    loadQueueMembers,
    createRecord,
  };
}

export type UseMyWorkResult = ReturnType<typeof useMyWork>;
